// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Fiscal/NovaNotaFiscal.tsx
//
//  Formulário de emissão de NF-e (modelo 55) em 6 abas, inspirado no
//  FrmNfeNovo do desktop:
//   1. Cabeçalho   — natureza, tipo, série, datas, finalidade, ambiente
//   2. Destinatário — cliente, CNPJ/CPF, IE, endereço
//   3. Produtos    — itens com tributação automática (ACBr)
//   4. Transporte  — modal frete, transportadora, placa, volumes
//   5. Faturamento — formas de pagamento (NF-e 4.0 obrigatório)
//   6. Obs/Totais  — observações fisco/contribuinte, frete/seguro/desconto, totais
//
//  Ações:
//   • "Salvar Rascunho"  → POST /api/notas-fiscais  (sem transmitir)
//   • "Salvar e Emitir"  → POST /api/notas-fiscais + POST /{id}/transmitir
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Plus, Trash2, RefreshCw, Zap,
  Send, Save, Truck, CreditCard,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { NaturezaOperacaoSearchCombobox } from '@/components/NaturezaOperacaoSearchCombobox';
import { NcmSearchCombobox } from '@/components/NcmSearchCombobox';
import { CfopSearchCombobox } from '@/components/CfopSearchCombobox';
import { ProdutoNome } from '@/components/ProdutoNome';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import { useCriarNotaFiscal } from '@/features/fiscal/hooks/useNotaFiscal';
import { notaFiscalService } from '@/features/fiscal/services/notaFiscalService';
import type {
  CriarNotaFiscalRequest,
  NotaAnaliticaItemRequest,
  TributacaoResponse,
  CalculoItemResultado,
  NotaTransporteInput,
  NotaVolumeInput,
  NotaPagamentoInput,
  NotaEntregaInput,
} from '@/features/fiscal/types';
import { FORMAS_PAGAMENTO_NFE, TPAG_COBRANCA, type NotaDuplicataInput } from '@/features/fiscal/types';
import { useFormasPagamentoCombo } from '@/features/financeiro/hooks/useCadastrosFinanceiros';
import { PessoaSearchCombobox } from '@/components/PessoaSearchCombobox';
import { ProdutoSearchCombobox } from '@/components/ProdutoSearchCombobox';
import { personService } from '@/features/pessoas/services/personService';
import type { EnderecoApi } from '@/features/pessoas/types';
import { apiClient, getApiErrorMessage } from '@/lib/apiClient';
import { ConfirmarTransmissaoDialog, avisoFreteSemTransporte } from '@/features/fiscal/components/ConfirmarTransmissaoDialog';
import { useAuth } from '@/hooks/useAuth';
import configuracoesService from '@/features/configuracoes/services/configuracoesService';

// ── tipos internos ────────────────────────────────────────────────────────────

interface ItemForm extends NotaAnaliticaItemRequest {
  _key:        number;
  _loadingTax: boolean;
  _trib?:      TributacaoResponse;
}

type Cabecalho = Omit<CriarNotaFiscalRequest, 'itens' | 'transporte' | 'volumes' | 'formasPagamento' | 'duplicatas'>;

// ── helpers ──────────────────────────────────────────────────────────────────

// Data de HOJE no fuso do navegador. toISOString() devolve a data em UTC: depois das 21h
// (horário de Brasília) o campo já sugeria o dia seguinte.
const today = () => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const fmtCur = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

// Tabela de CSOSN (Simples Nacional).
const CSOSN_VALIDOS = ['101', '102', '103', '201', '202', '203', '300', '400', '500', '900'];

// O campo "CST / CSOSN" da tela é um só. Gravar sempre em `cst` fazia um CSOSN digitado ("500")
// ir para o XML como origem 5 + CST 00. Item tributado pelo regime normal (o cálculo devolveu CST)
// segue em `cst`; nos demais, código da tabela do Simples vai para `csosn`.
const cstOuCsosn = (item: { _trib?: TributacaoResponse }, valor: string) => {
  const v = valor.trim() || undefined;
  const regimeNormal = !!item._trib?.cstAplicado;
  return !regimeNormal && v && CSOSN_VALIDOS.includes(v)
    ? { csosn: v, cst: undefined }
    : { cst: v, csosn: undefined };
};

let _key = 0;
const nextKey = () => ++_key;

const emptyItem = (): ItemForm => ({
  _key:          nextKey(),
  _loadingTax:   false,
  produtoId:     0,
  quantidade:    1,
  valorUnitario: 0,
  desconto:      0,
  cfop:          '',
  baseIcms: 0, aliqIcms: 0, valorIcms: 0,
  baseIcmsSt: 0, icmsSt: 0,
  difalDest: 0, difalOrig: 0, fcp: 0,
  valorIpi: 0,
  aliqPis: 0, valorPis: 0,
  aliqCofins: 0, valorCofins: 0,
  valorIbs: 0, valorCbs: 0, valorIs: 0,
  valorAproxTributos: 0,
});

const defaultCabecalho = (): Cabecalho => ({
  naturezaOperacao:   'Venda de mercadoria',
  tipoDocFiscal:      55,
  tipoNota:           1,
  numeroNota:         0,
  serie:              '1',
  dtMovimento:        today(),
  dtEmissao:          today(),
  dtSaida:            today(),
  clienteId:          0,
  tipoFrete:          9,
  isConsumidorFinal:  false,
  indicadorPresenca:  1,
  finalidadeId:       1,
  isAmbienteProducao: false,
  valorFrete:         0,
  valorSeguro:        0,
  valorDesconto:      0,
});

const defaultTransporte = (): NotaTransporteInput => ({
  nomeTransportadora: '',
  cnpjTransportadora: '',
  placaVeiculo:       '',
  ufVeiculo:          '',
  rntc:               '',
});

// ══════════════════════════════════════════════════════════════════════════════
//  ABA 1 — Cabeçalho
// ══════════════════════════════════════════════════════════════════════════════

function TabCabecalho({
  form,
  onChange,
}: {
  form:     Cabecalho;
  onChange: (patch: Partial<Cabecalho>) => void;
}) {
  // A NF-e grava o texto (natOp); o id só serve para o combobox mostrar a escolha.
  const [naturezaId, setNaturezaId] = useState<number | null>(null);
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
      <div className="md:col-span-2 space-y-1">
        <Label>Natureza da Operação *</Label>
        <NaturezaOperacaoSearchCombobox
          value={naturezaId}
          onSelect={(n) => {
            setNaturezaId(n?.id ?? null);
            onChange({ naturezaOperacao: n?.descricao ?? '' });
          }}
          placeholder={form.naturezaOperacao || 'Selecione a natureza da operação...'}
        />
      </div>

      <div className="space-y-1">
        <Label>Tipo Documento *</Label>
        <Select
          value={String(form.tipoDocFiscal)}
          onValueChange={(v) => onChange({ tipoDocFiscal: Number(v) })}
        >
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="55">NF-e (55)</SelectItem>
            <SelectItem value="65">NFC-e (65)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1">
        <Label>Entrada / Saída *</Label>
        <Select
          value={String(form.tipoNota)}
          onValueChange={(v) => onChange({ tipoNota: Number(v) })}
        >
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">Saída</SelectItem>
            <SelectItem value="2">Entrada</SelectItem>
          </SelectContent>
        </Select>
        {form.finalidadeId === 4 && (
          <p className="text-xs text-muted-foreground">
            Devolução de compra ao fornecedor é Saída; recebimento de
            devolução de venda é Entrada.
          </p>
        )}
      </div>

      <div className="space-y-1">
        <Label>Finalidade *</Label>
        <Select
          value={String(form.finalidadeId)}
          onValueChange={(v) => onChange({ finalidadeId: Number(v) })}
        >
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">Normal</SelectItem>
            <SelectItem value="2">Complementar</SelectItem>
            <SelectItem value="3">Ajuste</SelectItem>
            <SelectItem value="4">Devolução</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {(form.finalidadeId === 2 || form.finalidadeId === 4) && (
        <div className="space-y-1 md:col-span-2">
          <Label>Chave da NF-e referenciada *</Label>
          <Input
            value={form.chavesReferenciadas?.[0] ?? ''}
            onChange={(e) => {
              const chave = e.target.value.replace(/\D/g, '').slice(0, 44);
              onChange({ chavesReferenciadas: chave ? [chave, ...(form.chavesReferenciadas ?? []).slice(1)] : [] });
            }}
            inputMode="numeric"
            placeholder="44 dígitos da nota devolvida ou complementada"
          />
          <p className="text-xs text-muted-foreground">
            {(form.chavesReferenciadas?.[0] ?? '').length} / 44 dígitos — obrigatória para transmitir.
          </p>
        </div>
      )}

      <div className="space-y-1">
        <Label>Série</Label>
        <Input
          value={form.serie ?? ''}
          onChange={(e) => onChange({ serie: e.target.value || undefined })}
          placeholder="1"
          maxLength={3}
        />
      </div>

      <div className="space-y-1">
        <Label>Número da Nota</Label>
        <Input
          type="number"
          min={0}
          value={form.numeroNota || ''}
          onChange={(e) => onChange({ numeroNota: Number(e.target.value) })}
          placeholder="Auto"
        />
        <p className="text-xs text-muted-foreground">
          Em branco: numerada automaticamente na transmissão.
        </p>
      </div>

      <div className="space-y-1">
        <Label>Data Emissão</Label>
        <Input
          type="date"
          value={form.dtEmissao}
          onChange={(e) => onChange({ dtEmissao: e.target.value })}
        />
      </div>

      <div className="space-y-1">
        <Label>Data Movimento</Label>
        <Input
          type="date"
          value={form.dtMovimento}
          onChange={(e) => onChange({ dtMovimento: e.target.value })}
        />
      </div>

      <div className="space-y-1">
        <Label>Data Saída</Label>
        <Input
          type="date"
          value={form.dtSaida}
          onChange={(e) => onChange({ dtSaida: e.target.value })}
        />
      </div>

      <div className="space-y-1">
        <Label>Indicador Presença</Label>
        <Select
          value={String(form.indicadorPresenca)}
          onValueChange={(v) => onChange({ indicadorPresenca: Number(v) })}
        >
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">Presencial</SelectItem>
            <SelectItem value="2">Internet</SelectItem>
            <SelectItem value="3">Teleatendimento</SelectItem>
            <SelectItem value="4">NFC-e em domicílio</SelectItem>
            <SelectItem value="9">Outros</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-6 md:col-span-3 pt-1">
        <div className="flex items-center gap-2">
          <Switch
            checked={form.isConsumidorFinal}
            onCheckedChange={(v) => onChange({ isConsumidorFinal: v })}
          />
          <Label>Consumidor Final</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={form.isAmbienteProducao}
            onCheckedChange={(v) => onChange({ isAmbienteProducao: v })}
          />
          <Label>
            Produção
            {!form.isAmbienteProducao && (
              <Badge variant="outline" className="ml-2 text-yellow-600 border-yellow-500 text-xs">
                HOMOLOGAÇÃO
              </Badge>
            )}
          </Label>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  ABA 2 — Destinatário
// ══════════════════════════════════════════════════════════════════════════════

// Local de entrega: só quando a mercadoria vai para um endereço que não é o do destinatário.
function LocalDeEntrega({ form, onChange }: {
  form:     Cabecalho;
  onChange: (patch: Partial<Cabecalho>) => void;
}) {
  const entrega = form.entrega ?? null;
  const set = (patch: Partial<NotaEntregaInput>) => onChange({ entrega: { ...(entrega ?? {}), ...patch } });
  const campo = (chave: keyof NotaEntregaInput, rotulo: string, extra?: { placeholder?: string; maxLength?: number; classe?: string; maiusculo?: boolean }) => (
    <div className={`space-y-1 ${extra?.classe ?? ''}`}>
      <Label>{rotulo}</Label>
      <Input
        value={entrega?.[chave] ?? ''}
        maxLength={extra?.maxLength}
        placeholder={extra?.placeholder}
        onChange={(e) => set({ [chave]: (extra?.maiusculo ? e.target.value.toUpperCase() : e.target.value) || undefined })}
      />
    </div>
  );

  return (
    <div className="md:col-span-full space-y-3 rounded-md border p-3">
      <div className="flex items-center gap-3">
        <Switch
          id="entrega-outro-endereco"
          checked={!!entrega}
          onCheckedChange={(v) => onChange({ entrega: v ? {} : null })}
        />
        <Label htmlFor="entrega-outro-endereco" className="cursor-pointer">Entregar em outro endereço</Label>
        <span className="text-xs text-muted-foreground">
          {entrega ? 'A nota sai com o local de entrega abaixo.' : 'A entrega é no endereço do destinatário.'}
        </span>
      </div>

      {entrega && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {campo('endereco', 'Logradouro *', { classe: 'col-span-2 md:col-span-3', maxLength: 80 })}
          {campo('numero', 'Número', { maxLength: 50 })}
          {campo('complemento', 'Complemento', { classe: 'md:col-span-2', maxLength: 80 })}
          {campo('bairro', 'Bairro *', { classe: 'md:col-span-2', maxLength: 80 })}
          {campo('cidade', 'Cidade *', { classe: 'md:col-span-2', maxLength: 80 })}
          {campo('uf', 'UF *', { maxLength: 2, placeholder: 'MG', maiusculo: true })}
          {campo('cep', 'CEP', { placeholder: '00000-000', maxLength: 9 })}
          {campo('nome', 'Quem recebe (nome)', { classe: 'col-span-2 md:col-span-3', maxLength: 200 })}
          {campo('cnpjCpf', 'CNPJ / CPF de quem recebe', { classe: 'md:col-span-2', placeholder: 'Em branco = o do destinatário', maxLength: 20 })}
          {campo('fone', 'Telefone', { maxLength: 20 })}
        </div>
      )}
    </div>
  );
}

function TabDestinatario({
  form,
  onChange,
}: {
  form:     Cabecalho;
  onChange: (patch: Partial<Cabecalho>) => void;
}) {
  // Ao escolher o cliente, traz do cadastro o que sai na nota (continua editável).
  const selecionarCliente = async (id: number) => {
    onChange({ clienteId: id });
    try {
      const p = await personService.getById(id);
      let enderecos: EnderecoApi[] = p.enderecos ?? [];
      if (enderecos.length === 0) {
        const r = await apiClient.get<EnderecoApi[]>(`/api/clientes/${id}/enderecos`);
        enderecos = r.data ?? [];
      }
      const e = enderecos[0];
      onChange({
        clienteId:         id,
        nomeRazao:         p.nomeRazao || undefined,
        cnpjCpf:           p.cpfCnpj || undefined,
        inscricaoEstadual: p.inscricaoEstadual || undefined,
        email:             p.email || undefined,
        isConsumidorFinal: p.isConsumidorFinal,
        uf:                e?.uf || undefined,
        endereco:          e?.logradouro || undefined,
        numero:            e?.numero || undefined,
        bairro:            e?.bairro || undefined,
        cidade:            e?.cidade || undefined,
        cidadeIbge:        e?.cidadeIbgeId || undefined,
        cep:               e?.cep || undefined,
      });
    } catch {
      toast.error('Não foi possível carregar os dados do cliente. Preencha manualmente.');
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
      <div className="md:col-span-3 space-y-1">
        <Label>Cliente *</Label>
        <PessoaSearchCombobox
          value={form.clienteId || undefined}
          onChange={selecionarCliente}
          filter="cliente"
          placeholder="Buscar cliente pelo nome ou CPF/CNPJ..."
        />
      </div>

      <div className="md:col-span-2 space-y-1">
        <Label>Nome / Razão Social</Label>
        <Input
          value={form.nomeRazao ?? ''}
          onChange={(e) => onChange({ nomeRazao: e.target.value || undefined })}
        />
      </div>

      <div className="space-y-1">
        <Label>E-mail (recebe NF-e e boleto)</Label>
        <Input
          type="email"
          value={form.email ?? ''}
          onChange={(e) => onChange({ email: e.target.value || undefined })}
          placeholder="Do cadastro do cliente"
        />
      </div>

      <div className="space-y-1">
        <Label>CNPJ / CPF</Label>
        <Input
          value={form.cnpjCpf ?? ''}
          onChange={(e) => onChange({ cnpjCpf: e.target.value || undefined })}
          placeholder="00.000.000/0000-00"
        />
      </div>

      <div className="space-y-1">
        <Label>Inscrição Estadual</Label>
        <Input
          value={form.inscricaoEstadual ?? ''}
          onChange={(e) => onChange({ inscricaoEstadual: e.target.value || undefined })}
        />
      </div>

      <div className="space-y-1">
        <Label>UF</Label>
        <Input
          value={form.uf ?? ''}
          onChange={(e) => onChange({ uf: e.target.value.toUpperCase() || undefined, cidadeIbge: undefined })}
          maxLength={2}
          placeholder="UF"
        />
      </div>

      <div className="md:col-span-2 space-y-1">
        <Label>Endereço</Label>
        <Input
          value={form.endereco ?? ''}
          onChange={(e) => onChange({ endereco: e.target.value || undefined })}
        />
      </div>

      <div className="space-y-1">
        <Label>Número</Label>
        <Input
          value={form.numero ?? ''}
          onChange={(e) => onChange({ numero: e.target.value || undefined })}
        />
      </div>

      <div className="space-y-1">
        <Label>Bairro</Label>
        <Input
          value={form.bairro ?? ''}
          onChange={(e) => onChange({ bairro: e.target.value || undefined })}
        />
      </div>

      <div className="space-y-1">
        <Label>Cidade</Label>
        <Input
          value={form.cidade ?? ''}
          onChange={(e) => onChange({ cidade: e.target.value || undefined, cidadeIbge: undefined })}
        />
      </div>

      <div className="space-y-1">
        <Label>CEP</Label>
        <Input
          value={form.cep ?? ''}
          onChange={(e) => onChange({ cep: e.target.value || undefined })}
          placeholder="00000-000"
        />
      </div>

      <LocalDeEntrega form={form} onChange={onChange} />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  ABA 3 — Produtos / Itens
// ══════════════════════════════════════════════════════════════════════════════

function TabProdutos({
  itens,
  ufCliente,
  isConsumidorFinal,
  tipoNota,
  onChange,
}: {
  itens:             ItemForm[];
  ufCliente:         string;
  isConsumidorFinal: boolean;
  tipoNota:          number;
  onChange:          (itens: ItemForm[]) => void;
}) {
  const { user } = useAuth();
  const setItem =(key: number, patch: Partial<ItemForm>) =>
    onChange(itens.map((i) => (i._key === key ? { ...i, ...patch } : i)));

  const addItem    = () => onChange([...itens, emptyItem()]);
  const removeItem = (key: number) => onChange(itens.filter((i) => i._key !== key));

  // Ao escolher o produto, traz descrição e preço do cadastro e já calcula a tributação.
  const selecionarProduto = async (item: ItemForm, id: number) => {
    let doCadastro: Partial<ItemForm> = { produtoId: id };
    try {
      const { data: p } = await apiClient.get<{ descricao: string; precoPadrao: number }>(`/api/produtos/${id}`);
      doCadastro = { ...doCadastro, descricao: p.descricao || undefined, valorUnitario: p.precoPadrao ?? 0 };
    } catch {
      toast.error('Não foi possível carregar os dados do produto. Preencha manualmente.');
    }
    await carregarTributacao({ ...item, ...doCadastro }, doCadastro);
  };

  // `extra` acompanha cada gravação: setItem parte da lista deste render, então um
  // setItem separado antes seria sobrescrito pelos seguintes.
  const carregarTributacao = async (item: ItemForm, extra: Partial<ItemForm> = {}) => {
    if (!item.produtoId) {
      toast.error('Selecione o produto antes de calcular a tributação.');
      return;
    }
    if (!item.quantidade || item.quantidade <= 0) {
      if (Object.keys(extra).length > 0) setItem(item._key, extra);
      toast.error('Informe a quantidade antes de calcular a tributação.');
      return;
    }
    // Sem UF do destinatário o cálculo assumia 'MG' em silêncio (CFOP/DIFAL errados
    // para cliente de outro estado): exige a UF antes de calcular. O produto escolhido fica.
    if (!/^[A-Z]{2}$/.test(ufCliente.trim())) {
      if (Object.keys(extra).length > 0) setItem(item._key, extra);
      toast.error('Informe a UF do destinatário (aba Destinatário) antes de calcular a tributação.');
      return;
    }
    setItem(item._key, { ...extra, _loadingTax: true });
    try {
      // Regra fiscal do produto (NCM, CST/CSOSN e regime — a tela usa _trib para saber se é CST ou CSOSN).
      const trib = await notaFiscalService.getTributacao({
        produtoId:       item.produtoId,
        empresaId:       user?.empresa.id,
        notaSaida:       tipoNota !== 2,
        ufCliente:       ufCliente.trim(),
        consumidorFinal: isConsumidorFinal,
      });

      // Valores pelo BACKEND com o motor do Desktop: redução de base por UF e regras do
      // veículo usado (ICMS interno e PIS/COFINS sobre a MARGEM, alíquota interestadual).
      // Empresa = a do login. Se o cálculo falhar, cai no cálculo simples sobre o valor cheio.
      let c: CalculoItemResultado | null = null;
      try {
        c = await notaFiscalService.calcularItem({
          produtoId:           item.produtoId,
          notaSaida:           tipoNota !== 2,
          ufCliente:           ufCliente.trim(),
          consumidorFinal:     isConsumidorFinal,
          clienteContribuinte: !isConsumidorFinal,
          quantidade:          item.quantidade,
          valorUnitario:       item.valorUnitario,
          desconto:            item.desconto,
          cfop:                item.cfop || undefined,
        });
      } catch (e) {
        toast.warning(getApiErrorMessage(e, 'Cálculo completo indisponível: tributos calculados sobre o valor cheio.'));
      }

      if (c) {
        setItem(item._key, {
          ...extra,
          _loadingTax:   false,
          _trib:         trib,
          ncm:           trib.ncm ?? item.ncm ?? undefined,
          cfop:          c.cfop || String(trib.cfopAplicado) || item.cfop,
          cst:           c.cst ?? trib.cstAplicado ?? undefined,
          csosn:         c.csosn ?? trib.csosn ?? undefined,
          baseIcms:      c.baseIcms,
          aliqIcms:      c.aliqIcms,
          valorIcms:     c.valorIcms,
          percRedBcIcms: c.percRedBcIcms,
          baseIcmsSt:    c.baseIcmsSt,
          icmsSt:        c.valorIcmsSt,
          difalDest:     c.difalDest,
          difalOrig:     c.difalOrig,
          fcp:           c.fcp,
          valorIpi:      c.valorIpi,
          aliqPis:       c.aliqPis,
          valorPis:      c.valorPis,
          basePis:       c.basePis,
          aliqCofins:    c.aliqCofins,
          valorCofins:   c.valorCofins,
          baseCofins:    c.baseCofins,
          valorAproxTributos: c.valorAproxTributos,
        });
        if (c.ehVeiculoUsado) {
          toast.success(c.margem != null
            ? `Veículo usado: ICMS/PIS/COFINS sobre a margem de ${fmtCur(c.margem)} (custo ${c.fonteCusto === 'RENAVE' ? 'da compra no RENAVE' : 'médio do produto'}).`
            : 'Veículo usado sem custo de aquisição conhecido: tributos calculados sobre o valor cheio.');
        } else {
          toast.success('Tributação calculada.');
        }
        return;
      }

      const base = item.quantidade * item.valorUnitario - item.desconto;
      setItem(item._key, {
        ...extra,
        _loadingTax:  false,
        _trib:        trib,
        ncm:          trib.ncm ?? undefined,
        cfop:         String(trib.cfopAplicado),
        cst:          trib.cstAplicado ?? undefined,
        csosn:        trib.csosn ?? undefined,
        baseIcms:     base,
        aliqIcms:     trib.icmsAliquota,
        valorIcms:    (base * trib.icmsAliquota) / 100,
        aliqPis:      trib.pisAliquota,
        valorPis:     (base * trib.pisAliquota) / 100,
        aliqCofins:   trib.cofinsAliquota,
        valorCofins:  (base * trib.cofinsAliquota) / 100,
        valorIpi:     trib.incideIpi ? (base * trib.perIpi) / 100 : 0,
      });
    } catch (e) {
      setItem(item._key, { ...extra, _loadingTax: false });
      toast.error(getApiErrorMessage(e, 'Não foi possível calcular a tributação para este produto.'));
    }
  };

  return (
    <div className="space-y-4 pt-4">
      <div className="flex justify-end">
        <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={addItem}>
          <Plus className="h-3.5 w-3.5" /> Adicionar Item
        </Button>
      </div>

      {itens.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-10">
          Nenhum item adicionado. Clique em "Adicionar Item".
        </p>
      ) : (
        <>
          {itens.map((item, idx) => (
            <div key={item._key} className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-muted-foreground">Item {idx + 1}</span>
                <div className="flex gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="gap-1.5 text-xs"
                    onClick={() => carregarTributacao(item)}
                    disabled={item._loadingTax}
                  >
                    {item._loadingTax
                      ? <RefreshCw className="h-3 w-3 animate-spin" />
                      : <Zap className="h-3 w-3" />}
                    Calcular Tributação
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-destructive hover:text-destructive"
                    onClick={() => removeItem(item._key)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>

              {/* Dados básicos */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="col-span-2 md:col-span-4 space-y-1">
                  <Label className="text-xs">Produto *</Label>
                  <ProdutoSearchCombobox
                    value={item.produtoId || undefined}
                    onChange={(id) => selecionarProduto(item, id)}
                    placeholder="Buscar produto pela descrição ou código..."
                  />
                </div>
                <div className="col-span-2 md:col-span-3 space-y-1">
                  <Label className="text-xs">Descrição</Label>
                  <Input
                    value={item.descricao ?? ''}
                    onChange={(e) => setItem(item._key, { descricao: e.target.value || undefined })}
                    placeholder="Descrição do produto na nota"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Unidade</Label>
                  <Input
                    value={item.unidade ?? ''}
                    onChange={(e) => setItem(item._key, { unidade: e.target.value || undefined })}
                    placeholder="UN"
                    maxLength={6}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Quantidade *</Label>
                  <Input
                    type="number" step="0.0001" min={0.0001} required
                    value={item.quantidade}
                    onChange={(e) => setItem(item._key, { quantidade: Number(e.target.value) })}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Valor Unitário (R$) *</Label>
                  <Input
                    type="number" step="0.01" min={0} required
                    value={item.valorUnitario}
                    onChange={(e) => setItem(item._key, { valorUnitario: Number(e.target.value) })}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Desconto (R$)</Label>
                  <Input
                    type="number" step="0.01" min={0}
                    value={item.desconto}
                    onChange={(e) => setItem(item._key, { desconto: Number(e.target.value) })}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Total</Label>
                  <p className="text-sm font-semibold pt-2 pl-1">
                    {fmtCur(item.quantidade * item.valorUnitario - item.desconto)}
                  </p>
                </div>
              </div>

              {/* Fiscal */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 pt-1 border-t">
                <div className="space-y-1">
                  <Label className="text-xs">NCM</Label>
                  <NcmSearchCombobox
                    valueCodigo={item.ncm ?? undefined}
                    onChange={(_id, codigo) => setItem(item._key, { ncm: codigo || undefined })}
                    placeholder="Buscar NCM..."
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">CFOP *</Label>
                  <CfopSearchCombobox
                    value={item.cfop ? Number(item.cfop) : null}
                    onChange={(v) => setItem(item._key, { cfop: v ? String(v) : '' })}
                    placeholder="Buscar CFOP..."
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">CST / CSOSN</Label>
                  <Input
                    value={item.cst ?? item.csosn ?? ''}
                    onChange={(e) => setItem(item._key, cstOuCsosn(item, e.target.value))}
                    placeholder="00"
                    maxLength={3}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Alíq. ICMS (%)</Label>
                  <Input
                    type="number" step="0.01" min={0}
                    value={item.aliqIcms}
                    onChange={(e) => {
                      const aliq = Number(e.target.value);
                      const base = item.quantidade * item.valorUnitario - item.desconto;
                      setItem(item._key, { aliqIcms: aliq, baseIcms: base, valorIcms: (base * aliq) / 100 });
                    }}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Valor ICMS (R$)</Label>
                  <Input
                    type="number" step="0.01" min={0}
                    value={item.valorIcms}
                    onChange={(e) => setItem(item._key, { valorIcms: Number(e.target.value) })}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Alíq. PIS (%)</Label>
                  <Input
                    type="number" step="0.01" min={0}
                    value={item.aliqPis}
                    onChange={(e) => {
                      const aliq = Number(e.target.value);
                      const base = item.quantidade * item.valorUnitario - item.desconto;
                      setItem(item._key, { aliqPis: aliq, valorPis: (base * aliq) / 100 });
                    }}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Alíq. COFINS (%)</Label>
                  <Input
                    type="number" step="0.01" min={0}
                    value={item.aliqCofins}
                    onChange={(e) => {
                      const aliq = Number(e.target.value);
                      const base = item.quantidade * item.valorUnitario - item.desconto;
                      setItem(item._key, { aliqCofins: aliq, valorCofins: (base * aliq) / 100 });
                    }}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">IPI (R$)</Label>
                  <Input
                    type="number" step="0.01" min={0}
                    value={item.valorIpi}
                    onChange={(e) => setItem(item._key, { valorIpi: Number(e.target.value) })}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Informações adicionais do item</Label>
                <Input
                  value={item.infAdicional ?? ''}
                  onChange={(e) => setItem(item._key, { infAdicional: e.target.value || undefined })}
                  placeholder="Sai na nota junto do item (ex.: pedido de compra do cliente, lote, observação)"
                  maxLength={500}
                />
              </div>
            </div>
          ))}

          {itens.length > 1 && (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Produto</TableHead>
                    <TableHead className="text-right">Qtd</TableHead>
                    <TableHead className="text-right">V. Unit.</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">ICMS</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {itens.map((item, idx) => (
                    <TableRow key={item._key}>
                      <TableCell className="text-muted-foreground text-xs">{idx + 1}</TableCell>
                      <TableCell className="text-sm">{item.descricao ?? (item.produtoId ? <ProdutoNome id={item.produtoId} /> : '—')}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm">{item.quantidade}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm">{fmtCur(item.valorUnitario)}</TableCell>
                      <TableCell className="text-right tabular-nums font-medium text-sm">
                        {fmtCur(item.quantidade * item.valorUnitario - item.desconto)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm">{fmtCur(item.valorIcms)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  ABA 4 — Transporte
// ══════════════════════════════════════════════════════════════════════════════

function TabTransporte({
  tipoFrete,
  transporte,
  volumes,
  onFreteChange,
  onTranspChange,
  onVolumesChange,
}: {
  tipoFrete:       number;
  transporte:      NotaTransporteInput;
  volumes:         NotaVolumeInput[];
  onFreteChange:   (v: number) => void;
  onTranspChange:  (patch: Partial<NotaTransporteInput>) => void;
  onVolumesChange: (vols: NotaVolumeInput[]) => void;
}) {
  const addVolume = () =>
    onVolumesChange([...volumes, { quantidade: 1, especie: '', marca: '', pesoLiquido: 0, pesoBruto: 0 }]);

  const setVolume = (idx: number, patch: Partial<NotaVolumeInput>) =>
    onVolumesChange(volumes.map((v, i) => (i === idx ? { ...v, ...patch } : v)));

  const removeVolume = (idx: number) =>
    onVolumesChange(volumes.filter((_, i) => i !== idx));

  // Transportadora do cadastro de pessoas: preenche os campos (que seguem editáveis).
  const [transpPessoaId, setTranspPessoaId] = useState<number | undefined>();
  const selecionarTransportadora = async (pessoaId: number) => {
    setTranspPessoaId(pessoaId);
    try {
      const p = await personService.getById(pessoaId);
      const end = p.enderecos?.find((e) => e.tipo === 'comercial') ?? p.enderecos?.[0];
      onTranspChange({
        nomeTransportadora:      p.nomeRazao || undefined,
        cnpjTransportadora:      p.cpfCnpj || undefined,
        inscricaoEstadualTransp: p.inscricaoEstadual || undefined,
        ...(end ? {
          enderecoTransp: [end.logradouro, end.numero].filter(Boolean).join(', ') || undefined,
          cidadeTransp:   end.cidade || undefined,
          ufTransp:       end.uf || undefined,
        } : {}),
      });
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Não foi possível carregar a transportadora.'));
    }
  };

  return (
    <div className="space-y-6 pt-4">
      {/* Modal de Frete */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-1">
          <Label className="flex items-center gap-1.5">
            <Truck className="h-3.5 w-3.5" /> Modal de Frete
          </Label>
          <Select
            value={String(tipoFrete)}
            onValueChange={(v) => onFreteChange(Number(v))}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="9">Sem Frete</SelectItem>
              <SelectItem value="0">Por conta do Emitente (CIF)</SelectItem>
              <SelectItem value="1">Por conta do Destinatário (FOB)</SelectItem>
              <SelectItem value="2">Por conta de Terceiros</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {tipoFrete !== 9 && (
        <>
          {/* Dados da transportadora */}
          <div>
            <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">Transportadora</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3 space-y-1">
                <Label className="text-xs">Buscar no cadastro</Label>
                <PessoaSearchCombobox
                  value={transpPessoaId}
                  onChange={selecionarTransportadora}
                  placeholder="Buscar transportadora pelo nome ou CNPJ (preenche os campos abaixo)..."
                />
              </div>
              <div className="md:col-span-2 space-y-1">
                <Label className="text-xs">Nome / Razão Social</Label>
                <Input
                  value={transporte.nomeTransportadora ?? ''}
                  onChange={(e) => onTranspChange({ nomeTransportadora: e.target.value || undefined })}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">CNPJ / CPF</Label>
                <Input
                  value={transporte.cnpjTransportadora ?? ''}
                  onChange={(e) => onTranspChange({ cnpjTransportadora: e.target.value || undefined })}
                  placeholder="00.000.000/0000-00"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Inscrição Estadual</Label>
                <Input
                  value={transporte.inscricaoEstadualTransp ?? ''}
                  onChange={(e) => onTranspChange({ inscricaoEstadualTransp: e.target.value || undefined })}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Endereço</Label>
                <Input
                  value={transporte.enderecoTransp ?? ''}
                  onChange={(e) => onTranspChange({ enderecoTransp: e.target.value || undefined })}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Cidade</Label>
                <Input
                  value={transporte.cidadeTransp ?? ''}
                  onChange={(e) => onTranspChange({ cidadeTransp: e.target.value || undefined })}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">UF Transportadora</Label>
                <Input
                  value={transporte.ufTransp ?? ''}
                  onChange={(e) => onTranspChange({ ufTransp: e.target.value.toUpperCase() || undefined })}
                  maxLength={2}
                  placeholder="MG"
                />
              </div>
            </div>
          </div>

          {/* Veículo */}
          <div>
            <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">Veículo</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label className="text-xs">Placa</Label>
                <Input
                  value={transporte.placaVeiculo ?? ''}
                  onChange={(e) => onTranspChange({ placaVeiculo: e.target.value.toUpperCase() || undefined })}
                  placeholder="AAA-0000"
                  maxLength={8}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">UF Veículo</Label>
                <Input
                  value={transporte.ufVeiculo ?? ''}
                  onChange={(e) => onTranspChange({ ufVeiculo: e.target.value.toUpperCase() || undefined })}
                  maxLength={2}
                  placeholder="MG"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">RNTC</Label>
                <Input
                  value={transporte.rntc ?? ''}
                  onChange={(e) => onTranspChange({ rntc: e.target.value || undefined })}
                  placeholder="Registro Nacional de Transportador"
                />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Volumes */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Volumes</h3>
          <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={addVolume}>
            <Plus className="h-3.5 w-3.5" /> Adicionar Volume
          </Button>
        </div>
        {volumes.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            Nenhum volume informado.
          </p>
        ) : (
          <div className="space-y-3">
            {volumes.map((vol, idx) => (
              <div key={idx} className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-muted-foreground">Volume {idx + 1}</span>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6 text-destructive hover:text-destructive"
                    onClick={() => removeVolume(idx)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Quantidade</Label>
                    <Input
                      type="number" min={1}
                      value={vol.quantidade}
                      onChange={(e) => setVolume(idx, { quantidade: Number(e.target.value) })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Espécie</Label>
                    <Input
                      value={vol.especie ?? ''}
                      onChange={(e) => setVolume(idx, { especie: e.target.value || undefined })}
                      placeholder="CX, SC, etc."
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Marca</Label>
                    <Input
                      value={vol.marca ?? ''}
                      onChange={(e) => setVolume(idx, { marca: e.target.value || undefined })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Peso Líq. (kg)</Label>
                    <Input
                      type="number" step="0.001" min={0}
                      value={vol.pesoLiquido ?? ''}
                      onChange={(e) => setVolume(idx, { pesoLiquido: Number(e.target.value) || undefined })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Peso Bruto (kg)</Label>
                    <Input
                      type="number" step="0.001" min={0}
                      value={vol.pesoBruto ?? ''}
                      onChange={(e) => setVolume(idx, { pesoBruto: Number(e.target.value) || undefined })}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  ABA 5 — Faturamento / Pagamento
// ══════════════════════════════════════════════════════════════════════════════

function TabFaturamento({
  pagamentos,
  totalNota,
  onChange,
  duplicatas,
  onDuplicatasChange,
}: {
  pagamentos: NotaPagamentoInput[];
  totalNota:  number;
  onChange:   (pags: NotaPagamentoInput[]) => void;
  duplicatas: NotaDuplicataInput[];
  onDuplicatasChange: (d: NotaDuplicataInput[]) => void;
}) {
  const [novoFormaPag, setNovoFormaPag] = useState('01');
  const [novoValor, setNovoValor]       = useState('');
  // Forma do CADASTRO para pagamentos a prazo: decide o boleto automático na autorização.
  const [novoFormaCadastro, setNovoFormaCadastro] = useState('__none__');
  const { formasPagamento: formasCadastro = [] } = useFormasPagamentoCombo();
  const ehCobranca = TPAG_COBRANCA.includes(novoFormaPag);

  const valorCobranca = pagamentos.filter((p) => TPAG_COBRANCA.includes(p.formaPagamento)).reduce((s, p) => s + p.valor, 0);
  const formaCobranca = pagamentos.find((p) => TPAG_COBRANCA.includes(p.formaPagamento) && p.formaPagRecId)?.formaPagRecId ?? null;
  const somaParcelas  = duplicatas.reduce((s, d) => s + d.valor, 0);
  const [qtdParcelas, setQtdParcelas] = useState('1');
  const [primeiroVenc, setPrimeiroVenc] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10);
  });
  const [intervalo, setIntervalo] = useState('30');

  /** Divide o valor a prazo em N parcelas (centavos na última) a partir do 1º vencimento. */
  const gerarParcelas = (valor = valorCobranca) => {
    const n = Math.max(1, Math.min(60, parseInt(qtdParcelas) || 1));
    const dias = Math.max(1, parseInt(intervalo) || 30);
    const base = Math.floor((valor / n) * 100) / 100;
    const lista: NotaDuplicataInput[] = [];
    for (let i = 0; i < n; i++) {
      const d = new Date(`${primeiroVenc}T12:00:00`);
      d.setDate(d.getDate() + i * dias);
      lista.push({
        parcela: i + 1,
        dtVencimento: d.toISOString().slice(0, 10),
        valor: i === n - 1 ? Math.round((valor - base * (n - 1)) * 100) / 100 : base,
        formaPagRecId: formaCobranca,
      });
    }
    onDuplicatasChange(lista);
  };

  const alterarParcela = (idx: number, patch: Partial<NotaDuplicataInput>) =>
    onDuplicatasChange(duplicatas.map((d, i) => (i === idx ? { ...d, ...patch } : d)));

  const totalPago = pagamentos.reduce((s, p) => s + p.valor, 0);
  const troco     = Math.max(0, totalPago - totalNota);
  const restante  = Math.max(0, totalNota - totalPago);

  const addPagamento = () => {
    const valor = parseFloat(novoValor);
    if (!valor || valor <= 0) { toast.error('Informe um valor válido.'); return; }
    const forma = FORMAS_PAGAMENTO_NFE.find((f) => f.codigo === novoFormaPag);
    const formaPagRecId = ehCobranca && novoFormaCadastro !== '__none__' ? Number(novoFormaCadastro) : null;
    onChange([...pagamentos, {
      formaPagamento: novoFormaPag,
      nomePagamento:  forma?.descricao,
      valor,
      formaPagRecId,
    }]);
    setNovoValor('');
    // Primeira cobrança a prazo: já sugere 1 parcela em 30 dias (ajustável abaixo).
    if (ehCobranca && duplicatas.length === 0) {
      const d = new Date(`${primeiroVenc}T12:00:00`);
      onDuplicatasChange([{ parcela: 1, dtVencimento: d.toISOString().slice(0, 10), valor, formaPagRecId }]);
    }
  };

  const removePagamento = (idx: number) =>
    onChange(pagamentos.filter((_, i) => i !== idx));

  return (
    <div className="space-y-6 pt-4">
      <div className="flex items-end gap-3">
        <div className="space-y-1 flex-1">
          <Label className="flex items-center gap-1.5">
            <CreditCard className="h-3.5 w-3.5" /> Forma de Pagamento
          </Label>
          <Select value={novoFormaPag} onValueChange={setNovoFormaPag}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {FORMAS_PAGAMENTO_NFE.map((f) => (
                <SelectItem key={f.codigo} value={f.codigo}>
                  {f.codigo} — {f.descricao}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {ehCobranca && (
          <div className="space-y-1 flex-1">
            <Label>Forma do cadastro</Label>
            <Select value={novoFormaCadastro} onValueChange={setNovoFormaCadastro}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Nenhuma (sem boleto automático)</SelectItem>
                {formasCadastro.filter((f) => f.permiteRecebimento).map((f) => (
                  <SelectItem key={f.id} value={String(f.id)}>{f.descricao}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="space-y-1 w-40">
          <Label>Valor (R$)</Label>
          <Input
            type="number"
            step="0.01"
            min={0}
            value={novoValor}
            onChange={(e) => setNovoValor(e.target.value)}
            placeholder="0,00"
            onKeyDown={(e) => e.key === 'Enter' && addPagamento()}
          />
        </div>
        <Button type="button" size="sm" onClick={addPagamento} className="gap-1.5">
          <Plus className="h-3.5 w-3.5" /> Adicionar
        </Button>
      </div>

      {pagamentos.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">
          Nenhuma forma de pagamento adicionada.
          Para NF-e 4.0, ao menos uma forma é obrigatória.
        </p>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Forma</TableHead>
                <TableHead className="text-right">Valor</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {pagamentos.map((p, idx) => (
                <TableRow key={idx}>
                  <TableCell className="text-sm">{p.nomePagamento ?? p.formaPagamento}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{fmtCur(p.valor)}</TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => removePagamento(idx)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Parcelas de cobrança: viram duplicatas no XML e títulos/boletos na autorização */}
      {(valorCobranca > 0 || duplicatas.length > 0) && (
        <div className="rounded-lg border p-4 space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="font-medium text-sm flex-1 min-w-[180px]">
              Parcelas da cobrança
              <p className="text-xs text-muted-foreground font-normal">
                Saem na NF-e como duplicatas. Com forma do cadastro marcada como "Gerar boleto automático",
                cada parcela vira um boleto na autorização e o cliente recebe NF-e e boletos por e-mail.
              </p>
            </div>
            <div className="space-y-1 w-24">
              <Label className="text-xs">Parcelas</Label>
              <Input type="number" min={1} max={60} value={qtdParcelas} onChange={(e) => setQtdParcelas(e.target.value)} />
            </div>
            <div className="space-y-1 w-40">
              <Label className="text-xs">1º vencimento</Label>
              <Input type="date" value={primeiroVenc} onChange={(e) => setPrimeiroVenc(e.target.value)} />
            </div>
            <div className="space-y-1 w-28">
              <Label className="text-xs">A cada (dias)</Label>
              <Input type="number" min={1} value={intervalo} onChange={(e) => setIntervalo(e.target.value)} />
            </div>
            <Button type="button" size="sm" variant="outline" onClick={() => gerarParcelas()}>Gerar parcelas</Button>
          </div>
          {duplicatas.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20">Parcela</TableHead>
                  <TableHead>Vencimento</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {duplicatas.map((d, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="text-sm">{idx + 1}/{duplicatas.length}</TableCell>
                    <TableCell>
                      <Input type="date" className="h-8 w-40" value={d.dtVencimento.slice(0, 10)}
                        onChange={(e) => alterarParcela(idx, { dtVencimento: e.target.value })} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Input type="number" step="0.01" className="h-8 w-32 ml-auto text-right" value={d.valor}
                        onChange={(e) => alterarParcela(idx, { valor: parseFloat(e.target.value) || 0 })} />
                    </TableCell>
                    <TableCell>
                      <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive"
                        onClick={() => onDuplicatasChange(duplicatas.filter((_, i) => i !== idx).map((x, i) => ({ ...x, parcela: i + 1 })))}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {duplicatas.length > 0 && Math.abs(somaParcelas - valorCobranca) > 0.009 && (
            <p className="text-xs text-destructive">
              As parcelas somam {fmtCur(somaParcelas)}, mas o valor a prazo é {fmtCur(valorCobranca)}. Ajuste ou clique em "Gerar parcelas".
            </p>
          )}
        </div>
      )}

      {/* Resumo financeiro */}
      <div className="rounded-lg border p-4 bg-muted/40 space-y-2 max-w-xs">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Total da Nota</span>
          <span className="font-mono">{fmtCur(totalNota)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Total Pago</span>
          <span className="font-mono">{fmtCur(totalPago)}</span>
        </div>
        {restante > 0.005 && (
          <div className="flex justify-between text-sm text-red-500">
            <span>Restante</span>
            <span className="font-mono">{fmtCur(restante)}</span>
          </div>
        )}
        {troco > 0.005 && (
          <div className="flex justify-between text-sm text-green-600">
            <span>Troco</span>
            <span className="font-mono">{fmtCur(troco)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  ABA 6 — Observações e Totais
// ══════════════════════════════════════════════════════════════════════════════

function TabObservacoes({
  form,
  itens,
  onChange,
}: {
  form:     Cabecalho;
  itens:    ItemForm[];
  onChange: (patch: Partial<Cabecalho>) => void;
}) {
  const totalProdutos = itens.reduce((s, i) => s + i.quantidade * i.valorUnitario - i.desconto, 0);
  const totalIcms     = itens.reduce((s, i) => s + i.valorIcms, 0);
  const totalPis      = itens.reduce((s, i) => s + i.valorPis, 0);
  const totalCofins   = itens.reduce((s, i) => s + i.valorCofins, 0);
  const totalNota     = totalProdutos + form.valorFrete + form.valorSeguro - form.valorDesconto + (form.valorAcessorios ?? 0);

  return (
    <div className="space-y-6 pt-4">
      {/* Acréscimos / Deduções */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="space-y-1">
          <Label>Frete (R$)</Label>
          <Input
            type="number" step="0.01" min={0}
            value={form.valorFrete}
            onChange={(e) => onChange({ valorFrete: Number(e.target.value) })}
          />
        </div>
        <div className="space-y-1">
          <Label>Seguro (R$)</Label>
          <Input
            type="number" step="0.01" min={0}
            value={form.valorSeguro}
            onChange={(e) => onChange({ valorSeguro: Number(e.target.value) })}
          />
        </div>
        <div className="space-y-1">
          <Label>Desconto Geral (R$)</Label>
          <Input
            type="number" step="0.01" min={0}
            value={form.valorDesconto}
            onChange={(e) => onChange({ valorDesconto: Number(e.target.value) })}
          />
        </div>
      </div>

      {/* Observações */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label>Informações Adicionais ao Contribuinte</Label>
          <Textarea
            rows={3}
            value={form.obsContrib ?? ''}
            onChange={(e) => onChange({ obsContrib: e.target.value || undefined })}
          />
        </div>
        <div className="space-y-1">
          <Label>Informações ao Fisco</Label>
          <Textarea
            rows={3}
            value={form.obsFisco ?? ''}
            onChange={(e) => onChange({ obsFisco: e.target.value || undefined })}
          />
        </div>
      </div>

      {/* Totais */}
      <div className="rounded-lg border p-4 bg-muted/40 space-y-2 max-w-sm">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Produtos</span>
          <span className="font-mono">{fmtCur(totalProdutos)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">ICMS</span>
          <span className="font-mono">{fmtCur(totalIcms)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">PIS</span>
          <span className="font-mono">{fmtCur(totalPis)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">COFINS</span>
          <span className="font-mono">{fmtCur(totalCofins)}</span>
        </div>
        {form.valorFrete > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Frete</span>
            <span className="font-mono">+ {fmtCur(form.valorFrete)}</span>
          </div>
        )}
        {form.valorSeguro > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Seguro</span>
            <span className="font-mono">+ {fmtCur(form.valorSeguro)}</span>
          </div>
        )}
        {(form.valorAcessorios ?? 0) > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Outras despesas (acréscimo do pedido)</span>
            <span className="font-mono">+ {fmtCur(form.valorAcessorios ?? 0)}</span>
          </div>
        )}
        {form.valorDesconto > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Desconto Geral</span>
            <span className="font-mono text-red-500">- {fmtCur(form.valorDesconto)}</span>
          </div>
        )}
        <div className="flex justify-between font-semibold text-base border-t pt-2">
          <span>Total da Nota</span>
          <span className="font-mono text-primary">{fmtCur(totalNota)}</span>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  Página Principal
// ══════════════════════════════════════════════════════════════════════════════

export default function NovaNotaFiscal() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutate: criarMutate, isPending: isCriando } = useCriarNotaFiscal();

  // Com id na rota, a tela reabre um rascunho (ou nota rejeitada) para editar.
  const { id: idParam } = useParams<{ id: string }>();
  const notaId = idParam ? Number(idParam) : null;

  const [cab, setCab]               = useState<Cabecalho>(defaultCabecalho);
  const [itens, setItens]           = useState<ItemForm[]>([emptyItem()]);
  const [transporte, setTransporte] = useState<NotaTransporteInput>(defaultTransporte);
  const [volumes, setVolumes]       = useState<NotaVolumeInput[]>([]);
  const [pagamentos, setPagamentos] = useState<NotaPagamentoInput[]>([]);
  const [duplicatas, setDuplicatas] = useState<NotaDuplicataInput[]>([]);
  const [isEmitindo, setIsEmitindo] = useState(false);
  const [confirmarEmissao, setConfirmarEmissao] = useState(false);
  const [carregando, setCarregando] = useState(!!notaId);
  const [isSalvando, setIsSalvando] = useState(false);

  // Nota nova nasce no ambiente configurado no emitente; o interruptor continua valendo para
  // trocar numa nota específica. Sem resposta do serviço fiscal, fica em homologação.
  const { user } = useAuth();
  const empresaId = user?.empresa?.id;
  useEffect(() => {
    if (notaId || !empresaId) return;
    let ativo = true;
    configuracoesService.getEmitenteFiscal(empresaId)
      .then((e) => {
        if (!ativo || !e.cadastrado) return;
        setCab((c) => ({
          ...c,
          isAmbienteProducao: e.ambiente === 'producao' ? true : c.isAmbienteProducao,
          // Série configurada no emitente (o usuário ainda pode trocar na nota).
          serie: e.serieNfe > 0 && c.serie === '1' ? String(e.serieNfe) : c.serie,
        }));
      })
      .catch(() => { /* fica em homologação */ });
    return () => { ativo = false; };
  }, [notaId, empresaId]);

  useEffect(() => {
    if (!notaId) return;
    let ativo = true;
    setCarregando(true);
    notaFiscalService.getParaEdicao(notaId)
      .then((n) => {
        if (!ativo) return;
        const { itens: itensNota, transporte: transp, volumes: vols, formasPagamento, duplicatas: dups, ...cabecalho } = n;
        setDuplicatas((dups ?? []).map((d) => ({ ...d, dtVencimento: (d.dtVencimento ?? '').slice(0, 10) })));
        const dia = (v: string) => (v ?? '').slice(0, 10) || today();
        setCab({
          ...defaultCabecalho(), ...cabecalho,
          serie: cabecalho.serie ?? '1',
          dtMovimento: dia(cabecalho.dtMovimento), dtEmissao: dia(cabecalho.dtEmissao), dtSaida: dia(cabecalho.dtSaida),
        });
        setItens(itensNota.map((i) => ({ ...emptyItem(), ...i })));
        setTransporte({ ...defaultTransporte(), ...(transp ?? {}) });
        setVolumes(vols ?? []);
        // O servidor guarda o nome OU o código da forma no mesmo campo: recupera o código pelo nome.
        setPagamentos((formasPagamento ?? []).map((p) => {
          const forma = FORMAS_PAGAMENTO_NFE.find(
            (f) => f.codigo === p.formaPagamento || f.descricao === p.formaPagamento || f.descricao === p.nomePagamento);
          return forma
            ? { formaPagamento: forma.codigo, nomePagamento: forma.descricao, valor: p.valor, formaPagRecId: p.formaPagRecId }
            : { formaPagamento: p.formaPagamento, nomePagamento: p.nomePagamento, valor: p.valor, formaPagRecId: p.formaPagRecId };
        }));
      })
      .catch((e: unknown) => {
        toast.error(e instanceof Error ? e.message : 'Não foi possível abrir a nota para edição.');
        navigate(`/fiscal/notas-fiscais/${notaId}`);
      })
      .finally(() => { if (ativo) setCarregando(false); });
    return () => { ativo = false; };
  }, [notaId, navigate]);

  const patchCab        = (patch: Partial<Cabecalho>) => setCab((c) => ({ ...c, ...patch }));
  const patchTransporte = (patch: Partial<NotaTransporteInput>) => setTransporte((t) => ({ ...t, ...patch }));

  const totalProdutos = itens.reduce((s, i) => s + i.quantidade * i.valorUnitario - i.desconto, 0);
  const totalNota     = totalProdutos + cab.valorFrete + cab.valorSeguro - cab.valorDesconto + (cab.valorAcessorios ?? 0);

  const buildPayload = (): CriarNotaFiscalRequest => ({
    ...cab,
    transporte: cab.tipoFrete !== 9
      ? {
          nomeTransportadora:      transporte.nomeTransportadora || undefined,
          cnpjTransportadora:      transporte.cnpjTransportadora || undefined,
          inscricaoEstadualTransp: transporte.inscricaoEstadualTransp || undefined,
          enderecoTransp:          transporte.enderecoTransp || undefined,
          cidadeTransp:            transporte.cidadeTransp || undefined,
          ufTransp:                transporte.ufTransp || undefined,
          placaVeiculo:            transporte.placaVeiculo || undefined,
          ufVeiculo:               transporte.ufVeiculo || undefined,
          rntc:                    transporte.rntc || undefined,
        }
      : undefined,
    volumes:         volumes.filter((v) => v.quantidade > 0),
    formasPagamento: pagamentos,
    duplicatas,
    itens:           itens.map(({ _key, _loadingTax, _trib, ...rest }) => rest),
  });

  const validar = (): boolean => {
    if (!cab.clienteId) { toast.error('Informe o cliente (Destinatário).'); return false; }
    if (itens.length === 0) { toast.error('Adicione ao menos um item (Produtos).'); return false; }
    if (itens.some((i) => !i.cfop)) { toast.error('Informe o CFOP de todos os itens.'); return false; }
    // Parcelas de cobrança têm de fechar com o valor a prazo (viram duplicatas e boletos).
    const aPrazo = pagamentos.filter((p) => TPAG_COBRANCA.includes(p.formaPagamento)).reduce((s, p) => s + p.valor, 0);
    if (duplicatas.length > 0 && Math.abs(duplicatas.reduce((s, d) => s + d.valor, 0) - aPrazo) > 0.009) {
      toast.error('As parcelas da cobrança não fecham com o valor a prazo (aba Faturamento).');
      return false;
    }
    // Pagamentos informados têm de fechar com o total da nota (produtos + frete + seguro − desconto).
    // "Sem pagamento" (90) é a exceção: bonificação, remessa etc. vão com valor zero.
    if (pagamentos.length > 0 && !pagamentos.some((p) => p.formaPagamento === '90')) {
      const totalPago = pagamentos.reduce((s, p) => s + p.valor, 0);
      if (Math.abs(totalPago - totalNota) > 0.005) {
        toast.error(`Os pagamentos somam ${fmtCur(totalPago)} e a nota ${fmtCur(totalNota)}. Ajuste na aba Faturamento.`);
        return false;
      }
    }
    return true;
  };

  const invalidarNota = () =>
    queryClient.invalidateQueries({ predicate: (q) => JSON.stringify(q.queryKey).toLowerCase().includes('nota') });

  const handleSalvar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validar()) return;

    if (notaId) {
      setIsSalvando(true);
      try {
        await notaFiscalService.atualizar(notaId, buildPayload());
        await invalidarNota();
        toast.success('Alterações salvas.');
        navigate(`/fiscal/notas-fiscais/${notaId}`);
      } catch (err: unknown) {
        toast.error(err instanceof Error ? err.message : 'Não foi possível salvar as alterações.');
      } finally {
        setIsSalvando(false);
      }
      return;
    }

    criarMutate(buildPayload(), {
      onSuccess: (res) => {
        const num = res.numeroNota > 0 ? `Nº ${res.numeroNota}` : 'número automático na transmissão';
        toast.success(`Rascunho criado — ${num}, ${res.totalItens} item(s).`);
        // Abre o rascunho: é de lá que se confere, edita, transmite ou descarta.
        navigate(`/fiscal/notas-fiscais/${res.id}`);
      },
      onError: () => toast.error('Não foi possível criar o rascunho.'),
    });
  };

  const handleEmitir = () => {
    if (!validar()) return;
    setConfirmarEmissao(true);
  };

  const emitir = async () => {
    setConfirmarEmissao(false);
    setIsEmitindo(true);
    // Disparada a transmissão, o resultado é acompanhado no detalhe da nota: ficando nesta tela,
    // cada novo clique em "Salvar e Emitir" criava OUTRA nota, com outro número.
    let id = notaId;
    let transmissaoDisparada = false;
    try {
      // Usar o serviço diretamente para evitar double-toast do hook
      if (id) {
        await notaFiscalService.atualizar(id, buildPayload());
      } else {
        id = (await notaFiscalService.criar(buildPayload())).id;
      }
      toast.info('Rascunho salvo. Transmitindo para a SEFAZ...');

      transmissaoDisparada = true;
      const transmissao = await notaFiscalService.transmitir(id);
      if (transmissao.sucesso) {
        toast.success(`NF-e autorizada — Protocolo: ${transmissao.protocolo ?? '-'}`);
        const pos = transmissao.posAutorizacao;
        if (pos) {
          const ok = pos.titulos === 'OK' && pos.boletos === 'OK' && pos.email === 'OK';
          (ok ? toast.success : toast.warning)(
            ok ? `Boleto(s) registrado(s) e NF-e enviada para ${pos.emailDestino}.`
               : 'Cobrança da nota com pendência — veja "Cobrança e envio" no detalhe da nota.');
        }
      } else {
        toast.warning(`SEFAZ: ${transmissao.cStat} — ${transmissao.xMotivo}`);
      }
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e, 'Erro ao emitir a NF-e.'));
    } finally {
      setIsEmitindo(false);
      if (id && transmissaoDisparada) {
        await invalidarNota();
        navigate(`/fiscal/notas-fiscais/${id}`);
      }
    }
  };

  const isPending = isCriando || isEmitindo || isSalvando;

  if (carregando) {
    return (
      <div className="p-6 flex items-center gap-2 text-muted-foreground">
        <RefreshCw className="h-4 w-4 animate-spin" /> Carregando a nota…
      </div>
    );
  }

  return (
    <form onSubmit={handleSalvar} className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button type="button" variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground">{notaId ? 'Editar NF-e' : 'Nova NF-e'}</h1>
          <p className="text-sm text-muted-foreground">Emissão de Nota Fiscal Eletrônica (modelo 55)</p>
        </div>
        {!cab.isAmbienteProducao && (
          <Badge variant="outline" className="text-yellow-600 border-yellow-500">HOMOLOGAÇÃO</Badge>
        )}
      </div>

      {/* Abas */}
      <Card>
        <CardContent className="pt-6">
          <Tabs defaultValue="cabecalho">
            <TabsList className="grid grid-cols-6 w-full">
              <TabsTrigger value="cabecalho">Cabeçalho</TabsTrigger>
              <TabsTrigger value="destinatario">Destinatário</TabsTrigger>
              <TabsTrigger value="produtos">
                Produtos
                {itens.length > 0 && (
                  <Badge variant="secondary" className="ml-1.5 text-xs h-5 px-1.5">{itens.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="transporte">Transporte</TabsTrigger>
              <TabsTrigger value="faturamento">
                Faturamento
                {pagamentos.length > 0 && (
                  <Badge variant="secondary" className="ml-1.5 text-xs h-5 px-1.5">{pagamentos.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="observacoes">Obs / Totais</TabsTrigger>
            </TabsList>

            <TabsContent value="cabecalho">
              <TabCabecalho form={cab} onChange={patchCab} />
            </TabsContent>

            <TabsContent value="destinatario">
              <TabDestinatario form={cab} onChange={patchCab} />
            </TabsContent>

            <TabsContent value="produtos">
              <TabProdutos
                itens={itens}
                ufCliente={cab.uf ?? ''}
                isConsumidorFinal={cab.isConsumidorFinal}
                tipoNota={cab.tipoNota}
                onChange={setItens}
              />
            </TabsContent>

            <TabsContent value="transporte">
              <TabTransporte
                tipoFrete={cab.tipoFrete}
                transporte={transporte}
                volumes={volumes}
                onFreteChange={(v) => patchCab({ tipoFrete: v })}
                onTranspChange={patchTransporte}
                onVolumesChange={setVolumes}
              />
            </TabsContent>

            <TabsContent value="faturamento">
              <TabFaturamento
                pagamentos={pagamentos}
                totalNota={totalNota}
                onChange={setPagamentos}
                duplicatas={duplicatas}
                onDuplicatasChange={setDuplicatas}
              />
            </TabsContent>

            <TabsContent value="observacoes">
              <TabObservacoes form={cab} itens={itens} onChange={patchCab} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <ConfirmarTransmissaoDialog
        open={confirmarEmissao}
        producao={cab.isAmbienteProducao}
        avisos={[avisoFreteSemTransporte(cab.valorFrete, cab.tipoFrete)].filter((a): a is string => !!a)}
        onConfirmar={emitir}
        onFechar={() => setConfirmarEmissao(false)}
      />

      {/* Ações */}
      <div className="flex justify-end gap-3 pb-6">
        <Button type="button" variant="outline" onClick={() => navigate(-1)} disabled={isPending}>
          Cancelar
        </Button>
        <Button type="submit" variant="outline" disabled={isPending || itens.length === 0}>
          {(isCriando || isSalvando) && !isEmitindo
            ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Salvando...</>
            : <><Save className="h-4 w-4 mr-2" />{notaId ? 'Salvar alterações' : 'Salvar Rascunho'}</>}
        </Button>
        <Button
          type="button"
          disabled={isPending || itens.length === 0}
          onClick={handleEmitir}
        >
          {isEmitindo
            ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Emitindo...</>
            : <><Send className="h-4 w-4 mr-2" />Salvar e Emitir</>}
        </Button>
      </div>
    </form>
  );
}
